import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import {
  Activity, Building2, ClipboardList, FileWarning, Inbox, MapPin,
  ShieldAlert, UserCheck, AlertOctagon, AlarmClock, Clock, ChevronRight, Footprints, CalendarX,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SitesOverviewModal } from "@/components/SitesOverviewModal";
import { PeopleInPatrolsModal } from "@/components/PeopleInPatrolsModal";
import { SupervisorsOnDutyModal } from "@/components/SupervisorsOnDutyModal";
import { InstallPwaButton } from "@/components/InstallPwaButton";
import { PassiveGuardsUpcoming } from "@/components/PassiveGuardsUpcoming";
import {
  SEVERITY_LABEL, SEVERITY_COLOR, SERVICE_STATUS_LABEL,
  SCAN_STATUS_LABEL, SCAN_STATUS_COLOR,
  saludoMendoza, relojMendoza, fechaLargaMendoza,
} from "@/lib/i18n";

const AR_TZ = "America/Argentina/Buenos_Aires";
const todayAR = () => new Date().toLocaleDateString("en-CA", { timeZone: AR_TZ });

// Cards críticas (rojas) → arriba en hero
const CRITICAL = [
  { key: "panic_active",     testid: "panic-active",   label: "Alertas activas",          icon: AlertOctagon, to: "/admin/alertas",   help: "Botones SOS sin atender" },
  { key: "overdue_news",     testid: "overdue-news",   label: "Novedades vencidas",       icon: AlarmClock,   to: "/admin/novedades?overdue=1", help: "Seguimientos con deadline pasado" },
  { key: "overdue_patrols",  testid: "overdue-patrols",label: "Rondas no realizadas",     icon: CalendarX,    to: "/admin/rondas?status=vencida_mes", help: (m) => `No realizadas en ${m?.overdue_patrols_month_label || "el mes"}` },
  { key: "pending_patrols_today", testid: "pending-patrols-today", label: "Rondas pendientes", icon: Clock, to: () => `/admin/rondas?status=pendiente&date=${todayAR()}`, help: "Asignadas para hoy", neutral: true },
  { key: "week_anomalies",   testid: "week-anomalies", label: "Anomalías 7 días",         icon: ShieldAlert,  to: "/admin/rondas",    help: "Escaneos fuera de orden" },
];

// Cards operativas (en vivo)
const OPERATIVAS = [
  { key: "people_inside",       testid: "people-inside",       label: "Personas adentro",       icon: Footprints,  to: "/admin/visitas", help: "Faltan salir hoy" },
  { key: "vigiladores_en_ronda", testid: "in-progress-patrols", label: "Vigiladores en ronda",   icon: Activity,     modal: "people-in-patrols" },
  { key: "active_checkins",     testid: "active-checkins",     label: "Supervisores en marcha", icon: UserCheck,    modal: "supervisors-on-duty" },
  { key: "open_services",       testid: "open-services",       label: "Servicios abiertos",     icon: ClipboardList,to: "/admin/servicios" },
];

// Cards resumen del día
const RESUMEN = [
  { key: "today_completed_patrols", testid: "today-patrols",   label: "Rondas hoy completadas", icon: MapPin,      to: () => `/admin/rondas?status=completada&date=${todayAR()}` },
  { key: "today_news",              testid: "today-news",      label: "Novedades del día",      icon: FileWarning, to: "/admin/novedades" },
  { key: "pending_requests",        testid: "pending-requests",label: "Solicitudes pendientes", icon: Inbox,       to: "/admin/solicitudes" },
  { key: "active_clients",          testid: "active-clients",  label: "Clientes activos",       icon: Building2,   to: "/admin/clientes" },
  { key: "active_sites",            testid: "active-sites",    label: "Sedes activas",          icon: Building2,   to: "/admin/clientes" },
];

const ICON_BY_TYPE = { news: FileWarning, service: ClipboardList, scan: MapPin };

export default function AdminDashboard() {
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");
  const [now, setNow] = useState(new Date());
  const [overviewMode, setOverviewMode] = useState(null); // "sites" | "clients" | null
  const [activeModal, setActiveModal] = useState(null); // "people-in-patrols" | "supervisors-on-duty" | null
  const nav = useNavigate();
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/metrics");
      setM(data);
    } catch (e) { setErr(formatApiError(e)); toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    const j = setInterval(() => setNow(new Date()), 30000);
    return () => { clearInterval(i); clearInterval(j); };
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "Administradora";
  const saludo = saludoMendoza(now);
  const reloj = relojMendoza(now);
  const fecha = fechaLargaMendoza(now);

  return (
    <div style={{ paddingBottom: 60 }}>
      {/* HERO NEGRO TACTICAL — saludo + reloj Mendoza */}
      <section className="dash-hero" data-testid="dash-hero">
        <div className="dash-hero-inner">
          <div>
            <div className="dash-eyebrow">Centro de Operaciones · Mendoza, Argentina</div>
            <h1 className="dash-greeting">
              {saludo}, <span style={{ color: "#BF3437" }}>{firstName}</span>.
            </h1>
            <p className="dash-subgreet">Hoy es {fecha}. Acá está todo lo que está pasando ahora mismo.</p>
            <Link to="/admin/visitas" data-testid="admin-quick-ingreso" style={{
              display: "inline-flex", alignItems: "center", gap: 8, marginTop: 16,
              background: "#BF3437", color: "#FFFBF4", padding: "10px 18px", borderRadius: 4,
              fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em",
              textDecoration: "none",
            }}>
              <Footprints size={14} /> Cargar ingreso
            </Link>
          </div>
          <div className="dash-clock" data-testid="dash-clock">
            <Clock size={14} style={{ opacity: 0.6 }} />
            <span className="dash-clock-time">{reloj}</span>
            <span className="dash-clock-tz">Hora local · UTC−3</span>
          </div>
        </div>
        <div className="dash-hero-glow" />
      </section>

      <div style={{ padding: "12px 24px 0" }}><InstallPwaButton /></div>
      <div style={{ padding: "12px 24px 0" }}><PassiveGuardsUpcoming linkTo="/admin/guardias" /></div>

      <div style={{ padding: "0 24px" }}>
        {err && <div className="card" style={{ borderColor: "#BF3437", color: "#8E2629", marginTop: 18 }} data-testid="metrics-error">{err}</div>}

        {/* SECCIÓN: ALERTAS CRÍTICAS */}
        <SectionTitle title="Alertas críticas" subtitle="Lo que necesita tu atención ahora" tone="red" />
        <div className="dash-grid-5">
          {CRITICAL.map((c, idx) => (
            <CriticalCard key={c.key} testid={c.testid} label={c.label} icon={c.icon}
              help={typeof c.help === "function" ? c.help(m) : c.help} value={m?.[c.key]} loading={m == null}
              index={idx} neutral={c.neutral} onClick={() => nav(typeof c.to === "function" ? c.to() : c.to)} />
          ))}
        </div>

        {/* SECCIÓN: EN VIVO */}
        <SectionTitle title="En vivo" subtitle="Personas y servicios trabajando ahora" tone="bone" mt={36} />
        <div className="dash-grid-4">
          {OPERATIVAS.map((c, idx) => (
            <LiveCard key={c.key} testid={c.testid} label={c.label} icon={c.icon} value={m?.[c.key]} loading={m == null} index={idx}
              secondary={c.key === "vigiladores_en_ronda" && m ? `${m.vigiladores_en_ronda ?? 0} vigiladores · ${m.in_progress_patrols ?? 0} rondas en curso` : undefined}
              onClick={() => (c.modal ? setActiveModal(c.modal) : nav(c.to))} />
          ))}
        </div>

        {/* SECCIÓN: RESUMEN DEL DÍA */}
        <SectionTitle title="Resumen del día" subtitle="Actividad acumulada en el día" tone="bone" mt={36} />
        <div className="dash-grid-5">
          {RESUMEN.map((c, idx) => (
            <SummaryCard key={c.key} testid={c.testid} label={c.label} icon={c.icon} value={m?.[c.key]} loading={m == null} index={idx}
              onClick={() => (c.key === "active_sites" ? setOverviewMode("sites") : c.key === "active_clients" ? setOverviewMode("clients") : nav(typeof c.to === "function" ? c.to() : c.to))} />
          ))}
        </div>

        {/* TIMELINE DE ACTIVIDAD */}
        <SectionTitle title="Actividad reciente" subtitle="Últimos 15 eventos del equipo" tone="bone" mt={36} />
        <ActivityTimeline feed={m?.feed} loading={m == null} />
      </div>
      {overviewMode && <SitesOverviewModal mode={overviewMode} onClose={() => setOverviewMode(null)} />}
      {activeModal === "people-in-patrols" && <PeopleInPatrolsModal onClose={() => setActiveModal(null)} />}
      {activeModal === "supervisors-on-duty" && <SupervisorsOnDutyModal onClose={() => setActiveModal(null)} />}
    </div>

  );
}

// ---------- subcomponentes ------------------------------------------------

function SectionTitle({ title, subtitle, tone, mt = 24 }) {
  return (
    <div style={{ marginTop: mt, marginBottom: 14, display: "flex", alignItems: "baseline", gap: 14 }}>
      <div style={{
        width: 4, height: 22, background: tone === "red" ? "#BF3437" : "#0A0A0A", borderRadius: 2,
      }} />
      <div>
        <h2 style={{ fontSize: 17, fontWeight: 900, color: "#0A0A0A", margin: 0, letterSpacing: "-0.01em" }}>{title}</h2>
        <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "2px 0 0" }}>{subtitle}</p>
      </div>
    </div>
  );
}

function CriticalCard({ label, icon: Ic, value, loading, index, onClick, help, testid, neutral }) {
  const v = value ?? 0;
  const active = !neutral && v > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`metric-critical-${testid}`}
      className="dash-card-critical fade-in"
      style={{
        animationDelay: `${index * 60}ms`,
        background: active ? "linear-gradient(135deg, #BF3437 0%, #8E2629 100%)" : "#0A0A0A",
        color: "#FFFBF4",
        border: active ? "1px solid #BF3437" : "1px solid #1F1F1F",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", opacity: 0.85, fontWeight: 700 }}>{label}</span>
        <Ic size={18} style={{ opacity: active ? 1 : 0.5 }} />
      </div>
      {loading ? (
        <div className="skel" style={{ height: 48, background: "rgba(255,255,255,0.08)" }} />
      ) : (
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.04em" }}>{v}</span>
          {active && <span className="critical-dot" />}
        </div>
      )}
      <div style={{ marginTop: 12, fontSize: 11, opacity: 0.8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{help}</span>
        <ChevronRight size={14} />
      </div>
    </button>
  );
}

function LiveCard({ label, icon: Ic, value, loading, index, onClick, testid, secondary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`metric-live-${testid}`}
      className="dash-card-live fade-in"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span className="dash-live-dot" />
        <span style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--invict-text-muted)", fontWeight: 700 }}>EN VIVO</span>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 4 }}>
        {loading
          ? <div className="skel" style={{ height: 44, width: 70 }} />
          : <span style={{ fontSize: 44, fontWeight: 900, color: "#0A0A0A", lineHeight: 1, letterSpacing: "-0.03em" }}>{value ?? 0}</span>}
        <Ic size={20} style={{ color: "#BF3437" }} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0A0A0A", marginTop: 8 }}>{label}</div>
      {secondary && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }} data-testid={`metric-live-secondary-${testid}`}>{secondary}</div>}
    </button>
  );
}

function SummaryCard({ label, icon: Ic, value, loading, index, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`metric-summary-${testid}`}
      className="dash-card-summary fade-in"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <Ic size={15} style={{ color: "#6B655A" }} />
      </div>
      {loading
        ? <div className="skel" style={{ height: 30 }} />
        : <div style={{ fontSize: 30, fontWeight: 900, color: "#0A0A0A", lineHeight: 1, letterSpacing: "-0.02em" }}>{value ?? 0}</div>}
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--invict-text-muted)", marginTop: 8, letterSpacing: "0.04em", textTransform: "uppercase" }}>{label}</div>
    </button>
  );
}

function ActivityTimeline({ feed, loading }) {
  if (loading) return <div className="card" style={{ padding: 20 }}><div className="skel" style={{ height: 80 }} /></div>;
  if (!feed || feed.length === 0)
    return (
      <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--invict-text-muted)" }} data-testid="feed-empty">
        Sin actividad registrada todavía. Apenas tu equipo arranque a operar, vas a ver los eventos acá.
      </div>
    );
  return (
    <div className="dash-timeline" data-testid="dash-timeline">
      {feed.map((f, i) => <TimelineRow key={i} item={f} index={i} />)}
    </div>
  );
}

function TimelineRow({ item, index }) {
  const Ic = ICON_BY_TYPE[item.type] || Activity;
  const time = useMemo(() => new Date(item.at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }), [item.at]);
  const { label: badgeLabel, color: badgeColor, dotColor } = badgeFor(item);
  return (
    <div className="dash-timeline-row fade-in" style={{ animationDelay: `${index * 25}ms` }} data-testid={`feed-item-${index}`}>
      <div className="dash-timeline-rail">
        <div className="dash-timeline-node" style={{ background: dotColor }} />
      </div>
      <div className="dash-timeline-content">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <Ic size={13} style={{ color: "#BF3437" }} />
          <span style={{ fontWeight: 700, fontSize: 13, color: "#0A0A0A" }}>{item.title}</span>
          <span className={`badge ${badgeColor}`} style={{ marginLeft: "auto" }}>{badgeLabel}</span>
        </div>
        <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
          {item.actor} · <span style={{ fontVariantNumeric: "tabular-nums" }}>{time}</span>
        </div>
      </div>
    </div>
  );
}

function badgeFor(item) {
  if (item.type === "news") {
    return { label: SEVERITY_LABEL[item.extra] || "—", color: SEVERITY_COLOR[item.extra] || "gray", dotColor: severityDot(item.extra) };
  }
  if (item.type === "service") {
    return { label: SERVICE_STATUS_LABEL[item.extra] || item.extra, color: item.extra === "open" ? "blue" : "gray", dotColor: item.extra === "open" ? "#2E5BFF" : "#888" };
  }
  if (item.type === "scan") {
    return { label: SCAN_STATUS_LABEL[item.extra] || "—", color: SCAN_STATUS_COLOR[item.extra] || "gray", dotColor: item.extra === "ok" ? "#16a34a" : item.extra === "fuera_de_orden" ? "#BF3437" : "#888" };
  }
  return { label: "—", color: "gray", dotColor: "#888" };
}
function severityDot(s) { return s === "critical" ? "#BF3437" : s === "warning" ? "#D97706" : "#888"; }
